import { useCallback, useEffect, useRef, useState } from "react";
import { ChartDocument, Stroke } from "../types/chart";

const MAX_HISTORY = 50;

function cloneStrokes(strokes: Stroke[]): Stroke[] {
  return JSON.parse(JSON.stringify(strokes)) as Stroke[];
}

function strokesEqual(a: Stroke[], b: Stroke[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function createEmptyDocument(): ChartDocument {
  return {
    schemaVersion: 1,
    baseImageRef: "",
    baseImageSource: "preset",
    strokes: [],
  };
}

export function useChartDocument() {
  const [document, setDocument] = useState<ChartDocument>(createEmptyDocument);
  const [isDirty, setIsDirty] = useState(false);
  /** Strokes snapshot from last `loadDocument` / `markClean` / `reset` — `isDirty` stays false when current strokes match this. */
  const baselineStrokesRef = useRef<Stroke[]>([]);

  // Undo/redo stacks store snapshots of the strokes array
  const undoStack = useRef<Stroke[][]>([]);
  const redoStack = useRef<Stroke[][]>([]);

  useEffect(() => {
    setIsDirty(!strokesEqual(document.strokes, baselineStrokesRef.current));
  }, [document.strokes]);

  const pushUndo = useCallback((strokes: Stroke[]) => {
    undoStack.current = [...undoStack.current.slice(-MAX_HISTORY + 1), strokes];
    redoStack.current = [];
  }, []);

  const addStroke = useCallback(
    (stroke: Stroke) => {
      setDocument((prev) => {
        pushUndo(prev.strokes);
        return { ...prev, strokes: [...prev.strokes, stroke] };
      });
    },
    [pushUndo],
  );

  const removeStroke = useCallback(
    (strokeId: string) => {
      setDocument((prev) => {
        pushUndo(prev.strokes);
        return {
          ...prev,
          strokes: prev.strokes.filter((s) => s.id !== strokeId),
        };
      });
    },
    [pushUndo],
  );

  const updateStroke = useCallback(
    (strokeId: string, updates: Partial<Stroke>) => {
      setDocument((prev) => {
        pushUndo(prev.strokes);
        return {
          ...prev,
          strokes: prev.strokes.map((s) =>
            s.id === strokeId ? { ...s, ...updates } : s,
          ),
        };
      });
    },
    [pushUndo],
  );

  const undo = useCallback(() => {
    setDocument((prev) => {
      const snapshot = undoStack.current.pop();
      if (!snapshot) return prev;
      redoStack.current = [...redoStack.current, prev.strokes];
      return { ...prev, strokes: snapshot };
    });
  }, []);

  const redo = useCallback(() => {
    setDocument((prev) => {
      const snapshot = redoStack.current.pop();
      if (!snapshot) return prev;
      undoStack.current = [...undoStack.current, prev.strokes];
      return { ...prev, strokes: snapshot };
    });
  }, []);

  const canUndo = undoStack.current.length > 0;
  const canRedo = redoStack.current.length > 0;

  const setBaseImage = useCallback(
    (ref: string, source: ChartDocument["baseImageSource"]) => {
      setDocument((prev) => ({
        ...prev,
        baseImageRef: ref,
        baseImageSource: source,
      }));
    },
    [],
  );

  /**
   * Restore strokes to last saved / opened baseline (discard this session’s stroke edits).
   * Clears undo/redo so history does not resurrect discarded edits.
   */
  const revertStrokesToBaseline = useCallback(() => {
    const baseline = cloneStrokes(baselineStrokesRef.current);
    undoStack.current = [];
    redoStack.current = [];
    setDocument((prev) => ({ ...prev, strokes: baseline }));
  }, []);

  /**
   * Remove every annotation (empty canvas). Updates baseline to [] and clears undo/redo
   * so prior strokes cannot be restored from the toolbar undo stack.
   */
  const clearAllStrokesPermanently = useCallback(() => {
    undoStack.current = [];
    redoStack.current = [];
    baselineStrokesRef.current = [];
    setDocument((prev) => ({ ...prev, strokes: [] }));
  }, []);

  const reset = useCallback(() => {
    undoStack.current = [];
    redoStack.current = [];
    baselineStrokesRef.current = [];
    setDocument(createEmptyDocument());
    setIsDirty(false);
  }, []);

  const loadDocument = useCallback((doc: ChartDocument) => {
    undoStack.current = [];
    redoStack.current = [];
    baselineStrokesRef.current = cloneStrokes(doc.strokes);
    setDocument(doc);
    setIsDirty(false);
  }, []);

  const markClean = useCallback(() => {
    setDocument((prev) => {
      baselineStrokesRef.current = cloneStrokes(prev.strokes);
      return prev;
    });
    setIsDirty(false);
  }, []);

  /** Merges chart-level metadata (e.g. title shown in the editor header). */
  const updateDocumentMetadata = useCallback(
    (
      patch: Partial<NonNullable<ChartDocument["metadata"]>>,
      options?: { markDirty?: boolean },
    ) => {
      setDocument((prev) => ({
        ...prev,
        metadata: { ...(prev.metadata ?? {}), ...patch },
      }));
      if (options?.markDirty !== false) {
        setIsDirty(true);
      }
    },
    [],
  );

  const hasSessionStrokeEdits = !strokesEqual(
    document.strokes,
    baselineStrokesRef.current,
  );

  return {
    document,
    isDirty,
    /** True when current strokes differ from open/save baseline (ignores title-only edits). */
    hasSessionStrokeEdits,
    addStroke,
    removeStroke,
    updateStroke,
    undo,
    redo,
    canUndo,
    canRedo,
    setBaseImage,
    revertStrokesToBaseline,
    clearAllStrokesPermanently,
    reset,
    loadDocument,
    markClean,
    updateDocumentMetadata,
  };
}
